// 직원 권한(#196) — 손익 보이기 · 원가·단가표 수정 두 스위치 · 대표는 늘 전부 보고 고친다.

import { useState, useTransition } from "react";
import { toast } from "sonner";

import { InstantSaveNote } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Switch } from "@buildpy/ui/switch";
import { settleRedirect } from "@/lib/redirect-error";
import { saveCostAccess } from "@/api/actions/org";

interface Props {
  canEdit: boolean;
  staffView: boolean;
  staffEdit: boolean;
}

type SwitchKey = "view" | "edit";

const LABEL: Record<SwitchKey, { aria: string; on: string; off: string }> = {
  view: { aria: "직원에게 손익 보이기", on: "직원도 손익 탭·실제 원가를 봅니다", off: "손익 탭은 대표만 봅니다" },
  edit: { aria: "직원도 실제 원가·단가표 수정", on: "직원도 단가표·실제 원가를 고칩니다", off: "단가표·실제 원가는 대표만 고칩니다" },
};

const SettingsCostAccess = ({ canEdit, staffView, staffEdit }: Props) => {
  const [checked, setChecked] = useState<Record<SwitchKey, boolean>>({ view: staffView, edit: staffEdit });
  const [pending, startTransition] = useTransition();

  const onChange = (key: SwitchKey, next: boolean) => {
    const prev = checked[key];
    setChecked((cur) => ({ ...cur, [key]: next }));
    const rollback = () => setChecked((cur) => ({ ...cur, [key]: prev }));
    startTransition(async () => {
      try {
        const result = await saveCostAccess({ switch: key, on: next });
        if (result.ok) {
          toast.success(result.message);
        } else {
          rollback();
          toast.error(result.message);
        }
      } catch (caught) {
        if (settleRedirect(caught)) return;
        rollback();
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  const row = (key: SwitchKey) => (
    <label className="flex min-h-10 items-center gap-x-2 text-sm" htmlFor={`cost-access-${key}`}>
      <Switch
        aria-describedby={`cost-access-${key}-label`}
        aria-label={LABEL[key].aria}
        checked={checked[key]}
        disabled={!canEdit || pending}
        id={`cost-access-${key}`}
        onCheckedChange={(next) => onChange(key, next)}
      />
      <span id={`cost-access-${key}-label`}>{checked[key] ? LABEL[key].on : LABEL[key].off}</span>
    </label>
  );

  return (
    <SectionCard
      data-testid="settings-cost-access"
      description="대표는 늘 전부 보고 고칩니다. 실제 원가는 두 스위치가 다 켜져야 직원이 고칩니다. 마진 설정은 대표만 바꿉니다."
      actions={canEdit ? <InstantSaveNote /> : undefined}
      title="직원 권한">
      {row("view")}
      {row("edit")}
    </SectionCard>
  );
};

export default SettingsCostAccess;
